import React, { useState } from 'react';
import { Link, Plus, X } from 'lucide-react';

// Accept props: allTags, onAddTag, onDeleteTag
function WebLinks({ allTags = [], onAddTag, onDeleteTag }) {
  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);

  const handleAddLink = () => {
    if (!newLink) return;

    setLinks([
      ...links,
      {
        id: Date.now(),
        url: newLink,
        description: newDescription,
        tags: [...selectedTags],
        createdAt: new Date()
      }
    ]);

    // Reset form
    setNewLink('');
    setNewDescription('');
    setSelectedTags([]);
  };

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const removeLink = (id) => {
    setLinks(links.filter(link => link.id !== id));
  };

  const handleAddCustomTag = (e) => {
    e.preventDefault();
    const formattedTag = newTag.trim();
    if (!formattedTag) return;
    if (formattedTag.length > 25) {
      alert('Tag name cannot exceed 25 characters');
      return;
    }

    // Check if tag already exists (case-insensitive) in the passed list
    if (allTags.some(tag => tag.tag_name.toLowerCase() === formattedTag.toLowerCase())) {
      alert(`Tag "${formattedTag}" already exists.`);
    } else {
      // Call the prop function to add the tag globally
      if (onAddTag) {
          onAddTag(formattedTag);
      }
    }
    setNewTag('');
    setIsAddingTag(false);
  };

  const removeCustomTag = (tagToRemove) => {
    // Call the prop function to delete the tag globally
    if (onDeleteTag) {
      // Assuming tagToRemove is the tag name string
      onDeleteTag(tagToRemove);
    }
    // Also remove from local selection if it was selected
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="content-area">
      <div className="content-header">
        <h2>Web Links</h2>
        <p className="content-description">Save and organize interesting web links</p>
      </div>

      <div className="web-link-form">
        <div className="web-link-input-group">
          <Link size={18} />
          <input
            type="url"
            placeholder="Paste your link here..."
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            className="web-link-input"
          />
        </div>

        <textarea
          placeholder="Add a description..."
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          className="web-link-description"
        />

        <div className="web-link-tags-container">
          <div className="web-link-tags-header">
            <h3>Tags</h3>
            <button 
              className="add-tag-button"
              onClick={() => setIsAddingTag(true)}
            >
              <Plus size={16} />
              Add Tag
            </button>
          </div>

          {isAddingTag && (
            <form onSubmit={handleAddCustomTag} className="add-tag-form">
              <input
                type="text"
                placeholder="Enter new tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                className="new-tag-input input"
                maxLength={25}
              />
              <button type="submit" className="button icon-button primary-button">
                <Plus size={16} />
              </button>
              <button 
                type="button" 
                className="button icon-button"
                onClick={() => {
                  setIsAddingTag(false);
                  setNewTag('');
                }}
              >
                <X size={16} />
              </button>
            </form>
          )}

          <div className="web-link-tags">
            {allTags.map(tagObj => {
              const tagName = tagObj.tag_name; // Assuming the prop passes objects { tag_id, tag_name, usage_count }
              // Determine if tag is removable (e.g., if created by user - needs logic adjustment based on prop data)
              // For now, let's assume any tag can potentially be deleted via the button
              // A better approach might be to pass a flag indicating if a tag is deletable
              const isRemovable = true; // Placeholder - adjust based on actual data/requirements
              
              return (
                <div key={tagObj.tag_id || tagName} className="tag-button-container">
                  <button
                    className={`tag-button ${selectedTags.includes(tagName) ? 'active' : ''}`}
                    onClick={() => toggleTag(tagName)}
                  >
                    {tagName}
                  </button>
                  {isRemovable && (
                    <button 
                      className="remove-tag-button"
                      onClick={() => removeCustomTag(tagName)}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button className="add-link-button" onClick={handleAddLink}>
          <Plus size={18} />
          Add Link
        </button>
      </div>

      <div className="web-links-list">
        {links.map(link => (
          <div key={link.id} className="web-link-card">
            <div className="web-link-card-header">
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="web-link-url">
                {link.url}
              </a>
              <button className="remove-link-button" onClick={() => removeLink(link.id)}>
                <X size={16} />
              </button>
            </div>
            <p className="web-link-card-description">{link.description}</p>
            <div className="web-link-card-tags">
              {link.tags.map(tag => (
                <span key={tag} className="web-link-tag">{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WebLinks; 